import React, { useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Upload, FileText, AlertCircle, CheckCircle2 } from "lucide-react";

/**
 * Bulk lead-date editor (v1.4.3).
 *
 * Lets an admin upload a CSV of (phone, new_date) pairs to repair
 * lead_date on legacy bulk-imported leads when the source Google Sheet
 * has no per-row dates.
 *
 * Workflow:
 *   1. Pick CSV → preview rows
 *   2. Click "Dry run" → backend reports updated/not_found/ambiguous WITHOUT writing
 *   3. If the report looks good → click "Commit" → backend persists changes
 */
export default function BulkLeadDateEditor() {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const onPick = (f) => {
    setFile(f || null);
    setReport(null);
  };

  const submit = async (dryRun) => {
    if (!file) return toast.error("Pick a CSV first");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(
        `/admin/leads/bulk-date-update?dry_run=${dryRun}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setReport(data);
      if (dryRun) {
        toast.success(`Dry-run: would update ${data.updated} of ${data.processed}`);
      } else {
        toast.success(`Updated ${data.updated} leads`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="bulk-lead-date-editor">
      <div className="bg-card border border-border rounded-sm p-4">
        <div className="flex items-start gap-3">
          <FileText className="w-4 h-4 mt-0.5 text-primary" strokeWidth={1.75} />
          <div className="text-xs text-muted-foreground leading-relaxed flex-1">
            <div className="text-sm font-semibold text-foreground mb-1">Bulk lead-date editor</div>
            Upload a CSV with at minimum a <code className="font-mono text-[11px] bg-secondary px-1 rounded">phone</code> column
            and a <code className="font-mono text-[11px] bg-secondary px-1 rounded">date</code> (or <code className="font-mono text-[11px] bg-secondary px-1 rounded">lead_date</code>) column.
            Optional <code className="font-mono text-[11px] bg-secondary px-1 rounded">mapping_id</code> column disambiguates when the same phone exists across multiple Sheets.
            <br /><br />
            Match logic: <strong>mapping_id + phone_norm</strong> (precise) or <strong>phone_norm only</strong> (applies to all matches — be careful).
            Always run a <strong>dry-run first</strong> and review the report before committing.
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-sm p-4">
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => fileRef.current?.click()}
            data-testid="bulk-date-pick-file"
            className="h-9 px-3 text-xs font-medium border border-border bg-card hover:bg-secondary rounded-sm inline-flex items-center gap-1.5"
          >
            <Upload className="w-3.5 h-3.5" /> Pick CSV
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={(e) => onPick(e.target.files?.[0] || null)}
            data-testid="bulk-date-file-input"
          />
          {file && (
            <span className="text-xs text-muted-foreground" data-testid="bulk-date-file-name">
              <span className="font-medium text-foreground">{file.name}</span> · {(file.size / 1024).toFixed(1)} KB
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => submit(true)}
              disabled={!file || busy}
              data-testid="bulk-date-dry-run"
              className="h-9 px-3 text-xs font-medium border border-border rounded-sm hover:bg-secondary disabled:opacity-40 inline-flex items-center gap-1.5"
            >
              {busy ? "Working…" : "Dry run"}
            </button>
            <button
              onClick={() => submit(false)}
              disabled={!file || !report || busy}
              data-testid="bulk-date-commit"
              title={!report ? "Run a dry-run first" : "Apply the changes"}
              className="h-9 px-3 text-xs font-semibold bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 disabled:opacity-40 inline-flex items-center gap-1.5"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              Commit
            </button>
          </div>
        </div>
      </div>

      {report && (
        <div className="bg-card border border-border rounded-sm p-4" data-testid="bulk-date-report">
          <div className="flex items-center gap-2 mb-3">
            <span className={`text-[11px] uppercase font-mono px-2 py-0.5 rounded-sm ${report.dry_run ? "bg-[hsl(38_92%_50%)]/15 text-[hsl(38_92%_30%)]" : "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]"}`}>
              {report.dry_run ? "DRY RUN" : "COMMITTED"}
            </span>
            <span className="text-sm font-semibold">Report</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
            <Stat label="Processed" value={report.processed} />
            <Stat label="Updated" value={report.updated} tone="success" />
            <Stat label="Not found" value={report.not_found} tone={report.not_found ? "warning" : ""} />
            <Stat label="Ambiguous" value={report.ambiguous} tone={report.ambiguous ? "warning" : ""} />
            <Stat label="Errors" value={report.errors} tone={report.errors ? "critical" : ""} />
          </div>
          {report.samples && report.samples.length > 0 && (
            <div className="border-t border-border pt-3">
              <div className="label-mono mb-2 flex items-center gap-1.5">
                <AlertCircle className="w-3 h-3" /> Sample issues (first {report.samples.length})
              </div>
              <pre className="text-[11px] font-mono bg-secondary/30 p-2 rounded-sm overflow-x-auto max-h-48" data-testid="bulk-date-samples">{JSON.stringify(report.samples, null, 2)}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const cls = tone === "critical" ? "text-[hsl(var(--critical))]"
    : tone === "warning" ? "text-[hsl(38_92%_30%)]"
    : tone === "success" ? "text-[hsl(var(--success))]"
    : "";
  return (
    <div className="bg-secondary/30 border border-border rounded-sm p-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-lg font-light tabular ${cls}`}>{value}</div>
    </div>
  );
}
